import React from 'react';
import { Box, FormHelperText, IconButton, InputLabel, MenuItem, Typography } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { makeStyles } from 'tss-react/mui';
import { Control, Controller, useFieldArray, useWatch } from 'react-hook-form';
import { useLocales, useTheme } from '../../../../hooks';
import { markAsRequired } from '../../../../utils/formHelpers';
import FormControl from '../../FormControl';
import Select from '../../Select';
import FoldableSection from '../../FoldableSection';
import Button from '../../Button';
import { LokaliseAutocomplete } from '../../LokaliseAutocomplete';
import { enrichTestIds } from '../../TestsIds';
import { EnrichForm } from './Enrich';
import { ContentLabelEndDateField } from './ContentLabelEndDateField';
import { ContentLabelType } from './ContentLabelTypes';

interface ContentLabelsProps {
  control: Control<EnrichForm>;
  isSaving: boolean;
}

export const useStyles = makeStyles()((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(6),
    margin: theme.spacing(2),
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4)
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    padding: theme.spacing(2)
  },
  description: {
    padding: theme.spacing(2)
  },
  row: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 0.8fr) minmax(0, 1.1fr) minmax(0, 1fr) auto',
    gap: theme.spacing(4),
    alignItems: 'start'
  },
  removeButton: {
    marginTop: theme.spacing(2)
  },
  addButton: {
    alignSelf: 'flex-start'
  }
}));

export function ContentLabels({ control, isSaving }: ContentLabelsProps): JSX.Element {
  const { classes } = useStyles();
  const { formControlColor } = useTheme();
  const { t } = useLocales();

  const { fields, append, remove } = useFieldArray({ control, name: 'contentLabels' });
  const contentLabels = useWatch({ control, name: 'contentLabels' });

  const labelTypes = Object.values(ContentLabelType);
  // A label type only makes sense once per asset, so the ones already in the form are taken
  const takenLabels = (contentLabels ?? []).map(({ label }) => label);
  const availableLabels = labelTypes.filter((label) => !takenLabels.includes(label));

  const onAddContentLabel = () => {
    append({ label: availableLabels[0], endDate: '', displayText: '' });
  };

  return (
    <FoldableSection
      summary={<Typography variant="h6">{t('content.drawer.content_labels.foldable_title')}</Typography>}
      expanded={fields.length > 0}
      disableGutters
    >
      <div className={classes.container}>
        <Typography variant="body2" color="textSecondary" className={classes.description}>
          {t('content.drawer.content_labels.description')}
        </Typography>
        {fields.map((contentLabel, i) => (
          <Box key={contentLabel.id} className={classes.form}>
            <div className={classes.row}>
              <Controller
                control={control}
                name={`contentLabels.${i}.label`}
                rules={{ required: t('general.field_is_required') }}
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <FormControl fullWidth>
                    <InputLabel id={`contentLabelSelector-${i}`}>
                      {markAsRequired(t('content.drawer.content_labels.label'), true)}
                    </InputLabel>
                    <Select
                      label={markAsRequired(t('content.drawer.content_labels.label'), true)}
                      required
                      labelId={`contentLabelSelector-${i}`}
                      value={value}
                      onChange={({ target: { value } }) => onChange(value as ContentLabelType)}
                      data-testid={enrichTestIds.contentLabelSelector}
                      error={!!error}
                      disabled={isSaving}
                    >
                      {labelTypes.map((label) => (
                        <MenuItem
                          key={label}
                          value={label}
                          disabled={label !== value && takenLabels.includes(label)}
                          data-testid={enrichTestIds.contentLabelSelectorItem}
                          data-treatment-id={label}
                        >
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{t('content.drawer.content_labels.label_helper_text')}</FormHelperText>
                  </FormControl>
                )}
              />
              <Controller
                control={control}
                name={`contentLabels.${i}.endDate`}
                render={({ field: { value, onChange } }) => (
                  <ContentLabelEndDateField value={value} onChange={onChange} disabled={isSaving} />
                )}
              />
              <Controller
                control={control}
                name={`contentLabels.${i}.displayText`}
                render={({ field: { value, onChange } }) => (
                  <FormControl fullWidth>
                    <LokaliseAutocomplete
                      name={`contentLabels.${i}.displayText`}
                      label={t('content.drawer.content_labels.display_text')}
                      value={value ?? ''}
                      onChange={onChange}
                      required={false}
                      data-testid={enrichTestIds.contentLabelDisplayText}
                    />
                    <FormHelperText>{t('content.drawer.content_labels.display_text_helper_text')}</FormHelperText>
                  </FormControl>
                )}
              />
              <IconButton
                type="button"
                title={t('content.drawer.content_labels.actions.remove_content_label')}
                onClick={() => remove(i)}
                className={classes.removeButton}
                data-testid={enrichTestIds.contentLabelsRemoveButton}
                disabled={isSaving}
              >
                <Delete />
              </IconButton>
            </div>
          </Box>
        ))}
        <Button
          type="button"
          startIcon={<Add />}
          color={formControlColor}
          variant="outlined"
          onClick={onAddContentLabel}
          data-testid={enrichTestIds.contentLabelsAddButton}
          className={classes.addButton}
          disabled={isSaving || availableLabels.length === 0}
        >
          {t('content.drawer.content_labels.actions.add_content_label')}
        </Button>
      </div>
    </FoldableSection>
  );
}
